import React, { useMemo } from "react";
import { Box, Drawer, IconButton, Stack, Typography, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { fetchUsers } from "../../../fetchers/user";
import { useToolServiceHistory } from "../../../hooks/useToolServiceHistory";
import cmmsMessages from "../../cmms/cmmsMessages";
import ToolServiceHistoryPanel from "../../cmms/components/ToolServiceHistoryPanel";
// This drawer lives in features/tools but is CMMS chrome — it already borrowed the palette, and
// it mirrors the spare-part stock drawer's header. It takes the CMMS type scale for the same
// reason: a features/cmms* glob misses it, and then it is the one drawer that drifts.
import { cmmsTypeScale, getAssetHierarchyPalette, getPanelTitleSx } from "../../cmms/assetHierarchyStyles";

/*
 * The service history of one tool, in a drawer off the tool register.
 *
 * Owns both reads so that ToolServiceHistoryPanel stays presentational and features/tools stays
 * the only OEE page that knows CMMS exists. The hooks live here rather than in tools/index.jsx
 * for the same reason: an OEE page should not carry CMMS state it cannot use.
 *
 * Always mounted, opened on `tool`. That is what lets the drawer animate out — unmounting it on
 * close would make it vanish — while `enabled` keeps both queries from firing before a row is
 * clicked. A user who never opens the panel pays for nothing.
 */
export default function ToolServiceHistoryDrawer({ tool, onClose }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();

  const toolId = tool?.id ?? null;
  const open = Boolean(toolId);

  const history = useToolServiceHistory(toolId);

  // Same key and staleTime as the machine page's work order tab, so whichever renders first
  // warms the other. Gated on `open` because /tools has never needed the user list — without
  // it, every visit to the tool register would fetch users for a drawer nobody opened.
  const usersQuery = useQuery(["users"], () => fetchUsers(), {
    enabled: open,
    staleTime: 300000,
  });

  const userNameById = useMemo(() => {
    const map = new Map();
    (usersQuery.data ?? []).forEach((user) => {
      map.set(user.id, user.name ?? user.username ?? `#${user.id}`);
    });
    return map;
  }, [usersQuery.data]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, backgroundColor: cmmsColors.surface } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${cmmsColors.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Typography variant={cmmsTypeScale.panelTitle} sx={getPanelTitleSx(cmmsColors)}>
                {intl.formatMessage(cmmsMessages.toolHistoryTitle)}
              </Typography>
              <Typography variant="caption" sx={{ color: cmmsColors.mutedText }}>
                {tool?.name}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label={intl.formatMessage(cmmsMessages.close)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 1 }}>
          <ToolServiceHistoryPanel
            entries={history.entries}
            // The user list is folded into the same skeleton rather than raced against it:
            // both start when the drawer opens, and letting the entries land first would show
            // "#1016" for a moment before it turned into a name. A failed user read is not
            // waited on — isInitialLoading clears on error, and the ids are then the answer.
            isLoading={history.isLoading || usersQuery.isInitialLoading}
            isError={history.isError}
            error={history.error}
            onRetry={() => history.refetch()}
            isRetrying={history.isFetching}
            userNameById={userNameById}
          />
        </Box>
      </Box>
    </Drawer>
  );
}
